import { Reveal } from "@/components/Reveal";

const MARKETS = [
  { code: "UK", city: "London", x: "47%", y: "30%" },
  { code: "CAN", city: "Toronto", x: "22%", y: "34%" },
  { code: "USA", city: "New York", x: "27%", y: "44%" },
  { code: "UAE", city: "Dubai", x: "60%", y: "50%" },
  { code: "IN", city: "Mumbai · HQ", x: "67%", y: "54%", hq: true },
  { code: "SGP", city: "Singapore", x: "76%", y: "65%" },
  { code: "AUS", city: "Sydney", x: "86%", y: "82%" },
];

export default function Global() {
  return (
    <section
      id="global"
      data-testid="section-global"
      className="deck-section relative bg-black overflow-hidden"
    >
      {/* Cinematic radial glow */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            background:
              "radial-gradient(circle at 50% 55%, rgba(200,164,106,0.18) 0%, transparent 55%)",
          }}
        />
      </div>

      <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-[1600px] flex-col justify-between px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <Reveal>
          <div className="num-overline">10 · Global Vision</div>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <h2 className="font-heading text-ivory text-4xl md:text-5xl lg:text-[60px] leading-[1.05] tracking-tightest">
                Building the global
                <br />
                <span className="italic text-gold">
                  Indian luxury gifting house.
                </span>
              </h2>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="mt-8 font-body text-[15px] leading-[1.85] text-beige/75 max-w-md">
                From Mumbai to the world — partnering with luxury retailers
                across six key international markets.
              </p>
            </Reveal>

            <Reveal delay={0.4}>
              <div className="mt-12 flex flex-wrap gap-x-8 md:gap-x-10 gap-y-6 max-w-2xl">
                {["UK", "Canada", "USA", "UAE", "Singapore", "Australia"].map((c, i) => (
                  <div key={i} className="min-w-[80px]">
                    <div className="font-heading italic text-gold text-lg">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="mt-1.5 num-overline text-ivory/75 whitespace-nowrap">
                      {c}
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          {/* World map (stylized) */}
          <div className="lg:col-span-7">
            <Reveal delay={0.2}>
              <div className="relative aspect-[16/10] w-full">
                <WorldMap markers={MARKETS} />
              </div>
            </Reveal>
          </div>
        </div>

        <div className="num-overline text-ivory/40 self-end">
          Expanding · 2026 — 2028
        </div>
      </div>
    </section>
  );
}

// Minimalist stylized world map with luxury markers — pure SVG, no library dependency
function WorldMap({ markers }) {
  return (
    <div className="relative h-full w-full">
      <svg
        viewBox="0 0 1000 500"
        className="absolute inset-0 h-full w-full"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <radialGradient id="dotglow" cx="50%" cy="50%">
            <stop offset="0%" stopColor="#C8A46A" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#C8A46A" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Dotted continents (abstract luxury map) */}
        <g fill="#C8A46A" opacity="0.32">
          {Array.from({ length: 380 }).map((_, i) => {
            const cx = (i * 53) % 1000;
            const cy = ((i * 37) % 500) + (Math.sin(i * 0.7) * 18);
            // Land masses approximated with sin/cos bands
            const inLand =
              (cx > 80 && cx < 230 && cy > 80 && cy < 250) || // N America
              (cx > 240 && cx < 360 && cy > 280 && cy < 420) || // S America
              (cx > 430 && cx < 540 && cy > 70 && cy < 220) || // Europe
              (cx > 470 && cx < 620 && cy > 220 && cy < 380) || // Africa
              (cx > 560 && cx < 800 && cy > 100 && cy < 260) || // Asia
              (cx > 770 && cx < 900 && cy > 320 && cy < 420); // Australia
            if (!inLand) return null;
            return <circle key={i} cx={cx} cy={cy} r="2" />;
          })}
        </g>
      </svg>

      {/* Markers */}
      {markers.map((m, i) => (
        <div
          key={i}
          className="absolute"
          style={{ left: m.x, top: m.y, transform: "translate(-50%, -50%)" }}
        >
          <div className="relative flex flex-col items-center">
            {/* glow */}
            <span
              className="pointer-events-none absolute"
              style={{
                width: m.hq ? 80 : 56,
                height: m.hq ? 80 : 56,
                left: "50%",
                top: 0,
                transform: "translate(-50%, -50%)",
                background:
                  "radial-gradient(circle, rgba(200,164,106,0.55) 0%, rgba(200,164,106,0) 70%)",
                animation: "pulse 3.5s ease-in-out infinite",
              }}
            />
            <span
              className={`relative block ${m.hq ? "h-3 w-3" : "h-2 w-2"} bg-gold`}
              style={{ borderRadius: 0, boxShadow: "0 0 12px rgba(200,164,106,0.8)" }}
            />
            <span className="mt-3 whitespace-nowrap font-body text-[9px] uppercase tracking-[0.28em] text-ivory/90">
              {m.code}
            </span>
            <span className="mt-1 whitespace-nowrap font-heading italic text-gold/90 text-[11px]">
              {m.city}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}
